<template lang="pug">
system-tabs(section="/system/team")
    el-card.contains-table(shadow="always")
        .impersonation-sessions
            .error(v-if="loadError")
                Error(:message="loadError.message" :code="loadError.code")
            paginated-items(
                v-else
                :scope="scope"
                :type="key"
                :loading="loading"
                :items="paginated.items"
                :filters="filters"
                :show-filters="showFilters"
                :variables="paginated.variables"
                :all-columns="paginated.allColumns"
                :active-columns="paginated.activeColumns"
                :page-info="paginated.pageInfo"
                :sort="sort"
                @pagination-size-change="changePaginationSize"
                @pagination-current-change="changeCurrentPage"
                @show-table-columns="changeTableColumns"
                @change-filter="changeFilter"
                @change-sort="changeSort"
            )
                div(slot="top")
                    el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                        .left
                            router-link(:to="{ name: 'system-team' }")
                                el-button(size="mini" icon="el-icon-fa-arrow-left fas") {{ $t('Team') }}
                        .right.hide-on-empty
                            el-button(v-if="hasFilters" type="text" size="mini" @click="clearFilters") {{ $t('Reset filters') }}
                            el-button(
                                plain
                                size="mini"
                                type="primary"
                                icon="el-icon-fa-filter fas"
                                @click="toggleFilterVisiblity"
                            )
                                span(v-if="showFilters") {{ $t('Hide filters') }}
                                span(v-else) {{ $t('Show filters') }}

                template(v-slot:impersonator="{ item }")
                    span {{ personName(item.impersonator) }}
                    br
                    span.muted {{ personEmail(item.impersonator) }}

                template(v-slot:impersonated="{ item }")
                    span {{ personName(item.impersonated) }}
                    br
                    span.muted {{ personEmail(item.impersonated) }}

                //- A date-filtered column hands the slot an already formatted
                //- string; the raw timestamp is on the item.
                template(v-slot:started_at="{ item }")
                    span {{ formatTimestamp(item.started_at) }}

                //- Off the item, like started_at: for a date-range column the
                //- slot's `value` arrives already formatted (PaginationColumn).
                template(v-slot:last_seen_at="{ item }")
                    span {{ formatTimestamp(item.last_seen_at) }}

                template(v-slot:ended_at="{ item }")
                    span(v-if="item.ended_at") {{ formatTimestamp(item.ended_at) }}
                    span.muted(v-else) -

                template(v-slot:status="{ item }")
                    el-tag(v-if="!item.ended_at" type="success" size="mini") {{ $t('Open') }}
                    el-tooltip(v-else :content="endedBy(item)" placement="top")
                        el-tag(type="info" size="mini") {{ $t(item.ended_reason) }}

                template(v-slot:actions:th)
                    .col-right
                template(v-slot:actions:filter)
                    .col-right
                template(v-slot:actions="{ item }")
                    .col-right
                        el-button(
                            v-if="!item.ended_at"
                            size="mini"
                            type="danger"
                            plain
                            icon="el-icon-fa-right-from-bracket fas"
                            :loading="ending === item.id"
                            @click.stop="endSession(item)"
                        ) {{ $t('End session') }}
</template>

<i18n>
{
    "en": {
        "stopped": "Stopped",
        "revoked": "Revoked",
        "signed-out": "Signed out",
        "abandoned": "Abandoned",
        "Ended by {name}": "Ended by {name}",
        "Ended": "Ended"
    },
    "de": {
        "Team": "Team",
        "Reset filters": "Filter zurücksetzen",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Open": "Offen",
        "stopped": "Beendet",
        "revoked": "Widerrufen",
        "signed-out": "Abgemeldet",
        "abandoned": "Verwaist",
        "Ended by {name}": "Beendet von {name}",
        "Ended": "Beendet",
        "End session": "Sitzung beenden",
        "The session was ended.": "Die Sitzung wurde beendet.",
        "The session could not be ended.": "Die Sitzung konnte nicht beendet werden.",
        "Sessions could not be fetched.": "Die Sitzungen konnten nicht abgerufen werden.",
        "Forbidden": "Kein Zugriff"
    }
}
</i18n>

<script>
import _ from 'lodash';

import { timestamp2date } from '@/filters';
import ImpersonationSessionApi from '@/store/api/system/impersonationSession';
import { useSetupStore } from '@/stores';

import Error from '@/components/Error';
import PaginatedItems from '@/components/PaginatedItems';
import SystemTabs from '@/views/system/Partials/Tabs';

/**
 * Every mimic session there ever was, for whoever administers the team - and
 * the one thing they can do about an open one: end it. Whoever is inside the
 * account lands back in their own on their next request.
 *
 * The list is the server's audit trail; nothing here is kept in a store
 * because no other screen shows it.
 */
export default {
    name: 'ImpersonationSessions',
    components: {
        Error,
        PaginatedItems,
        SystemTabs,
    },
    data() {
        return {
            scope: 'base',
            key: 'impersonation_session',
            loading: false,
            loadError: null,
            ending: null,
            paginated: {
                variables: {
                    paginationSizes: [25, 50, 100],
                    order: 'started_at',
                    direction: 'desc',
                },
                filters: {},
                pageInfo: {
                    total: 0,
                    perPage: 25,
                    currentPage: 1,
                    lastPage: 0,
                },
                allColumns: [],
                activeColumns: [],
                showFilters: false,
                defaultSort: null,
                items: [],
            },
        };
    },
    computed: {
        filters() {
            return _.cloneDeep(this.paginated.filters);
        },
        hasFilters() {
            return !_.isEmpty(this.paginated.filters);
        },
        showFilters() {
            return this.hasFilters || this.paginated.showFilters;
        },
        sort() {
            const variables = this.paginated.variables;
            const defaultSort = this.paginated.defaultSort || { field: 'started_at', direction: 'desc' };

            return {
                field: variables.order || defaultSort.field,
                direction: variables.direction || defaultSort.direction,
            };
        },
    },
    methods: {
        personName(person) {
            return _.compact([_.get(person, 'first_name'), _.get(person, 'last_name')]).join(' ') || '-';
        },
        personEmail(person) {
            return _.get(person, 'email') || '';
        },
        formatTimestamp(timestamp) {
            return timestamp ? timestamp2date(timestamp, 'dd.MM.yyyy, HH:mm') : '-';
        },
        endedBy(item) {
            return item.endedBy ? this.$t('Ended by {name}', { name: this.personName(item.endedBy) }) : this.$t('Ended');
        },
        changePaginationSize(perPage) {
            this.paginated.pageInfo.perPage = perPage;
            this.refetch();
        },
        changeCurrentPage(currentPage) {
            this.paginated.pageInfo.currentPage = currentPage;
            this.refetch();
        },
        changeTableColumns(activeColumnKeys) {
            const activeColumns = this.paginated.allColumns.filter((column) => activeColumnKeys.indexOf(column.key) !== -1);

            if (activeColumns.length) this.paginated.activeColumns = activeColumns;
        },
        changeSort(key, value) {
            this.$set(this.paginated.variables, 'order', key);
            this.$set(this.paginated.variables, 'direction', value);
            this.refetch();
        },
        changeFilter(key, value, debounce = true) {
            this.paginated.pageInfo.currentPage = 1;

            const empty = value.match === '' && value.like === '' && value.from === '' && value.to === '' && (value.in && value.in.length === 0);
            if (empty) this.$delete(this.paginated.filters, key);
            else this.$set(this.paginated.filters, key, value);

            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        clearFilters() {
            this.paginated.filters = {};
            this.paginated.pageInfo.currentPage = 1;
            this.refetch();
        },
        toggleFilterVisiblity() {
            const value = !this.showFilters;

            this.paginated.showFilters = value;
            useSetupStore().saveFilterVisibility({ scope: this.scope, key: this.key, value });
        },
        refetch() {
            this.loading = true;

            return ImpersonationSessionApi.fetchSessionsPaginated({ scope: this.scope, type: this.key, ...this.paginated })
                .then((data) => {
                    this.loadError = null;
                    this.paginated.items = data.items;
                    this.paginated.pageInfo = _.cloneDeep(data.pageInfo);
                    this.paginated.allColumns = _.cloneDeep(data.config.allColumns);
                    this.paginated.showFilters = _.get(data.config, 'showFilters') || false;
                    this.paginated.defaultSort = _.get(data.config, 'defaultSort') || null;
                    this.changeTableColumns(_.map(data.config.activeColumnKeys, 'key'));
                })
                .catch((error) => {
                    console.error(error);

                    // A refusal has to look like one, the way the team list
                    // does it: "No records found" reads as an empty list.
                    const message = _.get(error, 'graphQLErrors.0.message');
                    if (message && /authoriz|forbidden/i.test(message)) {
                        this.loadError = { code: 403, message: this.$t('Forbidden') };
                        return;
                    }

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Sessions could not be fetched.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        debouncedRefetch: _.debounce(function () {
            this.refetch();
        }, 300),
        endSession(item) {
            this.ending = item.id;

            ImpersonationSessionApi.endSession(item.id)
                .then(() => {
                    this.$notify.success({
                        title: this.$t('The session was ended.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                    this.refetch();
                })
                .catch((error) => {
                    console.error(error);
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('The session could not be ended.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .finally(() => {
                    this.ending = null;
                });
        },
    },
    mounted() {
        this.refetch();
    },
};
</script>

<style lang="sass" scoped>
.table-top
    .left, .right
        display: flex
        align-items: center
        gap: 10px

.col-right
    text-align: right
</style>
